// app/javascript/controllers/field_cycles/report_map_controller.js

import { Controller } from "@hotwired/stimulus";
import {
  createMap,
  fitBounds,
  addReadOnlyGeomanControls,
  createLotLabel,
} from "../../helpers/map_helpers.js";
import { escapeHtml } from "../../utils.js";

const FALLBACK_COLOR = "#9CA3AF";
const METRIC_OPACITY = 0.65;

// Choropleth of a single field cycle's lots, colored by total actual/budget
// cost. Forked from lots--index-map: same Leaflet setup, but tag layers are
// dropped and the hover card is data-driven (popup.rows) instead of P&L fields.
// Connects to data-controller="field-cycles--report-map"
export default class extends Controller {
  static targets = [
    "map",
    "mapWrapper",
    "metricPill",
    "fullscreenIconOpen",
    "fullscreenIconClose",
  ];

  static values = {
    lots: Array,
    farmLat: Number,
    farmLon: Number,
    activeLayer: { type: String, default: "actual_cost" },
  };

  connect() {
    this.map = createMap(this.mapTarget, {
      lat: this.farmLatValue || 0,
      lon: this.farmLonValue || 0,
      zoom: 15,
    });

    this.#createHoverCard();

    this.polygons = this.lotsValue.map((lot) => {
      const geojson = JSON.parse(lot.polygon);
      const polygon = L.geoJSON(geojson, {
        style: { fillOpacity: METRIC_OPACITY, weight: 2 },
      }).addTo(this.map);

      polygon.on("mouseover", () => this.#showHoverCard(lot));
      polygon.on("mousemove", (event) => this.#positionHoverCard(event));
      polygon.on("mouseout", () => this.#hideHoverCard());
      polygon.on("click", () => {
        if (lot.url) window.location.href = lot.url;
      });

      const label = lot.name
        ? createLotLabel(this.map, lot.name, polygon, lot.color)
        : null;

      return { lot, polygon, label };
    });

    if (this.polygons.length > 0) {
      fitBounds(
        this.map,
        this.polygons.map((p) => p.polygon),
      );
    }

    addReadOnlyGeomanControls(this.map);

    this.#applyStyles();
  }

  disconnect() {
    this.hoverCardEl?.remove();
    this.polygons?.forEach(({ label }) => label?.remove?.());
    if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler);
      this.escHandler = null;
    }
    document.body.classList.remove("overflow-hidden");
  }

  selectMetric(event) {
    const metric = event.params.metric;
    if (!metric) return;
    this.activeLayerValue = metric;
    this.#applyStyles();
  }

  toggleFullscreen() {
    if (!this.hasMapWrapperTarget) return;
    const wrapper = this.mapWrapperTarget;
    const isFullscreen = wrapper.classList.toggle("lots-map-fullscreen");
    if (this.hasFullscreenIconOpenTarget)
      this.fullscreenIconOpenTarget.classList.toggle("hidden", isFullscreen);
    if (this.hasFullscreenIconCloseTarget)
      this.fullscreenIconCloseTarget.classList.toggle("hidden", !isFullscreen);
    document.body.classList.toggle("overflow-hidden", isFullscreen);
    requestAnimationFrame(() => this.map.invalidateSize());

    if (isFullscreen) {
      this.escHandler = (event) => {
        if (event.key === "Escape") this.toggleFullscreen();
      };
      document.addEventListener("keydown", this.escHandler);
    } else if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler);
      this.escHandler = null;
    }
  }

  activeLayerValueChanged() {
    this.#updatePillStyles();
  }

  // ===== private =====

  #updatePillStyles() {
    const activeLayer = this.activeLayerValue;
    this.metricPillTargets.forEach((pill) => {
      const isActive = pill.dataset.metric === activeLayer;
      pill.classList.toggle("btn-primary", isActive);
      pill.classList.toggle("btn-ghost", !isActive);
    });
  }

  #applyStyles() {
    this.polygons.forEach(({ lot, polygon }) => {
      polygon.setStyle(this.#styleFor(lot));
    });
    if (this.activeHoverLot) {
      this.hoverCardEl.innerHTML = this.#renderTooltipHtml(this.activeHoverLot);
    }
  }

  #styleFor(lot) {
    const color =
      lot.layers?.[this.activeLayerValue]?.color || lot.color || FALLBACK_COLOR;
    return { fillColor: color, color, fillOpacity: METRIC_OPACITY, weight: 2 };
  }

  #createHoverCard() {
    const el = document.createElement("div");
    el.className = "hidden lot-map-hover-card";
    el.style.cssText = [
      "position: fixed",
      "pointer-events: none",
      "z-index: 10000",
      "background: hsl(0 0% 100%)",
      "border: 1px solid hsl(220 13% 91%)",
      "border-radius: 8px",
      "box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12)",
      "padding: 10px 12px",
      "max-width: 280px",
      "transition: opacity 80ms ease-out",
      "opacity: 0",
    ].join(";");
    document.body.appendChild(el);
    this.hoverCardEl = el;
  }

  #showHoverCard(lot) {
    this.activeHoverLot = lot;
    this.hoverCardEl.innerHTML = this.#renderTooltipHtml(lot);
    this.hoverCardEl.classList.remove("hidden");
    this.hoverCardEl.style.opacity = "1";
  }

  #hideHoverCard() {
    this.activeHoverLot = null;
    this.hoverCardEl.classList.add("hidden");
    this.hoverCardEl.style.opacity = "0";
  }

  #positionHoverCard(event) {
    const orig = event.originalEvent;
    if (!orig) return;
    const offsetX = 14;
    const offsetY = 14;
    const cardWidth = this.hoverCardEl.offsetWidth;
    const cardHeight = this.hoverCardEl.offsetHeight;
    let left = orig.clientX + offsetX;
    let top = orig.clientY + offsetY;
    if (left + cardWidth > window.innerWidth - 8) {
      left = orig.clientX - cardWidth - offsetX;
    }
    if (top + cardHeight > window.innerHeight - 8) {
      top = orig.clientY - cardHeight - offsetY;
    }
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    this.hoverCardEl.style.left = `${left}px`;
    this.hoverCardEl.style.top = `${top}px`;
  }

  #renderTooltipHtml(lot) {
    const popup = lot.popup;
    if (!popup) {
      return `
        <div class="text-xs leading-relaxed" style="min-width: 160px">
          <div class="font-semibold text-sm">${escapeHtml(lot.name)}</div>
        </div>
      `;
    }

    const rowsHtml = (popup.rows || [])
      .map(
        ({ label, value, negative }) =>
          `<div class="flex justify-between gap-3"><span class="text-base-content/60">${escapeHtml(
            label,
          )}</span><span class="font-mono${negative ? " text-error" : ""}">${escapeHtml(value)}</span></div>`,
      )
      .join("");

    return `
      <div class="text-xs leading-relaxed" style="min-width: 200px">
        <div class="font-semibold text-sm mb-1.5">${escapeHtml(popup.name)}</div>
        <div class="space-y-0.5">${rowsHtml}</div>
      </div>
    `;
  }
}
